import { useEffect, useState } from 'react';
import { upperFirst } from 'lodash';
// mui
import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TableRow,
  TableCell,
  TextField,
  Typography,
} from '@mui/material';
// components
import { Iconify, Label } from '@/components';
import DialogSimple from '@/components/dialog/DialogSimple';
import { TableMoreMenu } from '@/components/table';
// hooks + utils
import { use3DotMenu } from '@/hooks/use3DotMenu';
import useToggle from '@/hooks/useToggle';
import { fDate } from '@/utils/formatTime';
import { getWalletDisplayName } from '../../utils';
// api + types
import type { WalletUpdateDto } from '@/features/users/shared/api';
import type { Wallet } from '@/features/users/shared/types';

type WalletTableRowProps = {
  row: Wallet;
  isUpdating: boolean;
  isDeleting: boolean;
  onUpdateRow: (walletId: number, walletData: WalletUpdateDto) => Promise<void>;
  onSetDefaultRow: (walletId: number) => void;
  onDeleteRow: (walletId: number) => Promise<void>;
};

type WalletFormErrors = {
  exp_month?: string;
  exp_year?: string;
};

type WalletEditDialogProps = {
  wallet: Wallet;
  open: boolean;
  isSubmitting: boolean;
  onClose: VoidFunction;
  onSave: (walletData: WalletUpdateDto) => Promise<void>;
};

function WalletEditDialog({
  wallet,
  open,
  isSubmitting,
  onClose,
  onSave,
}: WalletEditDialogProps) {
  const [name, setName] = useState(wallet.name ?? '');
  const [expMonth, setExpMonth] = useState(wallet.exp_month ?? '');
  const [expYear, setExpYear] = useState(wallet.exp_year ?? '');
  const [isDefault, setIsDefault] = useState(Boolean(wallet.default));
  const [errors, setErrors] = useState<WalletFormErrors>({});

  useEffect(() => {
    if (!open) return;

    setName(wallet.name ?? '');
    setExpMonth(wallet.exp_month ?? '');
    setExpYear(wallet.exp_year ?? '');
    setIsDefault(Boolean(wallet.default));
    setErrors({});
  }, [open, wallet]);

  const handleSave = async () => {
    const month = Number(expMonth);
    const year = expYear.trim();
    const nextErrors: WalletFormErrors = {};

    if (!Number.isInteger(month) || month < 1 || month > 12) {
      nextErrors.exp_month = 'Enter a month from 1 to 12';
    }

    if (!/^\d{4}$/.test(year)) {
      nextErrors.exp_year = 'Enter a four digit year';
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length) return;

    try {
      await onSave({
        name: name.trim(),
        exp_month: String(month),
        exp_year: year,
        default: isDefault,
      });
      onClose();
    } catch {
      // Parent mutation handles snackbar feedback.
    }
  };

  return (
    <DialogSimple
      dialogTitle="Edit wallet item"
      open={open}
      onClickButtonClose={onClose}
      onClickButtonLoading={handleSave}
      loadingButton
      loadingButtonText="Save"
      disagreeButton
      disagreeButtonText="Cancel"
      disagreeButtonColor="inherit"
      isSubmitting={isSubmitting}
    >
      <Stack spacing={2} sx={{ px: 3 }}>
        <TextField
          label="Custom name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={getWalletDisplayName(wallet)}
          fullWidth
        />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label="Expiry month"
            value={expMonth}
            onChange={(event) => setExpMonth(event.target.value)}
            error={Boolean(errors.exp_month)}
            helperText={errors.exp_month}
            fullWidth
          />
          <TextField
            label="Expiry year"
            value={expYear}
            onChange={(event) => setExpYear(event.target.value)}
            error={Boolean(errors.exp_year)}
            helperText={errors.exp_year}
            fullWidth
          />
        </Stack>

        <FormControlLabel
          control={
            <Checkbox
              checked={isDefault}
              onChange={(event) => setIsDefault(event.target.checked)}
            />
          }
          label="Set as default payment method"
        />
      </Stack>
    </DialogSimple>
  );
}

export default function ClientWalletTableRow({
  row,
  isUpdating,
  isDeleting,
  onUpdateRow,
  onSetDefaultRow,
  onDeleteRow,
}: WalletTableRowProps) {
  const {
    brand,
    exp_month,
    exp_year,
    default: isDefaultCard,
    last_used,
  } = row;
  const walletDisplayName = getWalletDisplayName(row);
  const { openMenu, handleCloseMenu, handleOpenMenu } = use3DotMenu();
  const {
    toggle: isEditDialogOpen,
    onOpen: openEditDialog,
    onClose: closeEditDialog,
  } = useToggle();
  const {
    toggle: isDeleteDialogOpen,
    onOpen: openDeleteDialog,
    onClose: closeDeleteDialog,
  } = useToggle();

  const handleDelete = async () => {
    try {
      await onDeleteRow(row.id);
      closeDeleteDialog();
    } catch {
      // Parent mutation handles snackbar feedback.
    }
  };

  return (
    <>
      <WalletEditDialog
        wallet={row}
        open={isEditDialogOpen}
        isSubmitting={isUpdating}
        onClose={closeEditDialog}
        onSave={(walletData) => onUpdateRow(row.id, walletData)}
      />

      <DialogSimple
        dialogTitle="Delete wallet item"
        open={isDeleteDialogOpen}
        onClickButtonClose={closeDeleteDialog}
        onClickButtonLoading={handleDelete}
        loadingButton
        loadingButtonText="Delete"
        loadingButtonColor="error"
        disagreeButton
        disagreeButtonText="Cancel"
        disagreeButtonColor="inherit"
        isSubmitting={isDeleting}
      >
        <Box sx={{ px: 3 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will detach the saved payment method from the client.
          </Alert>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to delete{' '}
            <span style={{ fontWeight: 600 }}>{walletDisplayName}</span>?
          </Typography>
        </Box>
      </DialogSimple>

      <TableRow hover sx={{ tableLayout: 'fixed' }}>
        <TableCell width="2%" />
        <TableCell
          align="left"
          sx={{ letterSpacing: row.name ? 0 : 1, fontWeight: 500 }}
          width="34%"
        >
          {walletDisplayName}
          {isDefaultCard && (
            <Label color="info" sx={{ ml: 0.5 }}>
              Default
            </Label>
          )}
        </TableCell>
        <TableCell align="left" width="18%">
          {upperFirst(brand)}
        </TableCell>
        <TableCell
          align="left"
          width="18%"
        >{`${exp_month}/${exp_year}`}</TableCell>
        <TableCell align="left" width="18%">
          {last_used ? fDate(last_used) : 'N/A'}
        </TableCell>
        <TableCell align="right" width="10%">
          <TableMoreMenu
            ariaLabel={`Open wallet actions for ${walletDisplayName}`}
            open={openMenu}
            onOpen={handleOpenMenu}
            onClose={handleCloseMenu}
            actions={
              <>
                {!isDefaultCard && (
                  <MenuItem
                    onClick={() => {
                      onSetDefaultRow(row.id);
                      handleCloseMenu();
                    }}
                  >
                    <Iconify icon="eva:star-outline" />
                    Set default
                  </MenuItem>
                )}
                <MenuItem
                  onClick={() => {
                    openEditDialog();
                    handleCloseMenu();
                  }}
                >
                  <Iconify icon="eva:edit-fill" />
                  Edit
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    openDeleteDialog();
                    handleCloseMenu();
                  }}
                  sx={{ color: 'error.main' }}
                >
                  <Iconify icon="eva:trash-2-outline" />
                  Delete
                </MenuItem>
              </>
            }
          />
        </TableCell>
      </TableRow>
    </>
  );
}
